import React from 'react';
import { ExternalLink } from 'lucide-react';
import type { EpisodeSummaryResponse } from '../../services/api/types.gen';
import { formatDateForDisplay } from '../../utils/dateUtils';
import { episodeAgainstChartsFrom } from '../episode/chartMovedAt';
import {
  MANAGED_IN_EXTERNAL_EMR_EPISODE_BANNER,
  visitsBeforeDayNote,
} from '../task/externalCustody';

/**
 * A banner over work the External EMR still charts (EMR-2889). It warns and
 * never blocks: the page stays editable.
 * Renders nothing unless `show` is true.
 */
export const ManagedInExternalEmrNotice: React.FC<{
  show: boolean | null | undefined;
  message: string;
}> = ({ show, message }) => {
  if (show !== true) return null;
  return (
    <div
      role='status'
      className='flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900'
      data-testid='managed-in-external-emr-notice'
    >
      <ExternalLink className='h-4 w-4 shrink-0' aria-hidden='true' />
      <span>{message}</span>
    </div>
  );
};

type EpisodeChartsFrom = Pick<
  EpisodeSummaryResponse,
  'start_date' | 'end_date' | 'roger_charts_from'
>;

/** The episode banner: shown when the episode ended before its charting day. */
export const EpisodeManagedInExternalEmrNotice: React.FC<{
  episode: EpisodeChartsFrom | null | undefined;
}> = ({ episode }) => (
  <ManagedInExternalEmrNotice
    show={!!episode && episodeAgainstChartsFrom(episode) === 'ended_before'}
    message={MANAGED_IN_EXTERNAL_EMR_EPISODE_BANNER}
  />
);

/** The goals note on the episode whose days straddle its charting day. */
export const GoalsChartsFromNote: React.FC<{
  episode: EpisodeChartsFrom | null | undefined;
}> = ({ episode }) => {
  if (!episode?.roger_charts_from) return null;
  if (episodeAgainstChartsFrom(episode) !== 'contains') return null;
  return (
    <p
      className='px-1 pb-2 text-sm text-muted-foreground'
      data-testid='goals-charts-from-note'
    >
      {visitsBeforeDayNote(formatDateForDisplay(episode.roger_charts_from))}
    </p>
  );
};
